import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash, Plus, Tag, Pencil, Search, Info, User, Edit, Badge, Loader2 } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import AddCategoryForm from '@/components/agenda/AddCategoryForm';
import AddSchoolCategoryForm from '@/components/agenda/AddSchoolCategoryForm';
import EditSchoolCategoryForm from '@/components/agenda/EditSchoolCategoryForm';
import EditCategoryForm from '@/components/agenda/EditCategoryForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/use-mobile';
import { BASE_URL } from '@/config/api';
import { useQueryClient } from '@tanstack/react-query';
import { eventService } from '@/services/eventService';

interface Category {
  id: string;
  name: string;
  color: string;
  price?: number;
  paymentType?: number;
  s_std_pay_type?: string;
  s_tch_pay_type?: string;
  s_thr_pay_type?: string; // Handle potential backend typo
  prices?: Record<string, number>;
  prices2?: Record<string, number>;
  studentPaymentType?: number;
  teacherPaymentType?: number;
}

interface CategoriesManagerProps {
  small?: boolean;
  onCategoriesLoad?: (count: number) => void;
}

const CategoriesManager: React.FC<CategoriesManagerProps> = ({ small = false, onCategoriesLoad }) => {
  const { token, user, refreshUserData } = useAuth();
  const { t } = useLanguage();
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const queryClient = useQueryClient();


  const fetchCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const response = await fetch(`${BASE_URL}/api/categories`, {
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const cats = data.categories || [];
        setCategories(cats);
        setFilteredCategories(cats);
        // Call the callback with categories count
        if (onCategoriesLoad) {
          onCategoriesLoad(cats.length);
        }
      } else {
        toast({
          title: "Failed to fetch categories",
          description: "There was an error loading the categories list.",
          variant: "destructive"
        });
        if (onCategoriesLoad) {
          onCategoriesLoad(0);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: "Failed to fetch categories",
        description: "There was an error loading the categories list.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [token]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter(category =>
        category.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  }, [searchQuery, categories]);

  const handleAddCategory = async (
    name: string,
    color: string,
    paymentType: number,
    prices: Record<string, number>
  ) => {
    if (!name.trim()) {
      toast({
        title: "Category name required",
        description: "Please enter a name for the category",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/add_category`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: name.trim(),
          color: color,
          paymentType: paymentType,
          prices: prices
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add category');
      }

      fetchCategories();

      // Refresh user data so wizard flags (hasCreatedCategory) update across UI
      try { await refreshUserData?.(); } catch {}

      toast({
        title: "Category added",
        description: `${name} has been added to your categories list`
      });

      setIsAddingCategory(false);
    } catch (error) {
      console.error('Error adding category:', error);
      toast({
        title: "Failed to add category",
        description: "There was an error adding the category. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSchoolCategory = async (
    name:string,
    color: string,
    paymentTypeStudent: number,
    paymentTypeTeacher: number,
    pricesStudent: Record<string, number>,
    pricesTeacher: Record<string, number>
  ) => {
    if (!name.trim()) {
      toast({
        title: "Category name required",
        description: "Please enter a name for the category",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/add_school_category`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: name.trim(),
          color: color,
          paymentTypeStudent: paymentTypeStudent,
          paymentTypeTeacher: paymentTypeTeacher,
          pricesStudent,
          pricesTeacher
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Validation errors:', errorData);
        throw new Error('Failed to add category');
      }

      fetchCategories();

      toast({
        title: "Category added",
        description: `${name} has been added to your categories list`
      });

      setIsAddingCategory(false);
    } catch (error) {
      console.error('Error adding category:', error);
      toast({
        title: "Failed to add category",
        description: "There was an error adding the category. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCategory = async (updatedCategory: Category) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/category/update`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id: updatedCategory.id,
          name: updatedCategory.name,
          color: updatedCategory.color,
          paymentType: updatedCategory.paymentType,
          prices: updatedCategory.prices
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update category');
      }

      fetchCategories();
      eventService.clearEventsCache();
      queryClient.clear();

      toast({
        title: t('categories.updated'),
        description: `${updatedCategory.name} ${t('categories.hasBeenUpdated')}`
      });

      setEditingCategory(null);
    } catch (error) {
      console.error('Error updating category:', error);
      /*toast({
        title: "Failed to update category",
        description: "There was an error updating the category. Please try again.",
        variant: "destructive"
      });*/
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSchoolCategory = async (updatedCategory: Category) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/school/category/update`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id: updatedCategory.id,
          name: updatedCategory.name,
          color: updatedCategory.color,
          paymentTypeStudent: updatedCategory.studentPaymentType,
          paymentTypeTeacher: updatedCategory.teacherPaymentType,
          pricesStudent: updatedCategory.prices ?? {},
          pricesTeacher: updatedCategory.prices2 ?? {}
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update category');
      }

      fetchCategories();
      eventService.clearEventsCache();
      queryClient.clear();

      toast({
        title: "Category updated",
        description: `${updatedCategory.name} has been updated`
      });

      setEditingCategory(null);
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: "Failed to update category",
        description: "There was an error updating the category. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    const category = categories.find(category => category.id === id);
    if (category) {
      setCategoryToDelete(category);
      setIsDeleteDialogOpen(true);
    }
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await fetch(`${BASE_URL}/api/delete_category`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id: categoryToDelete.id })
      });

      if (!response.ok) {
        throw new Error('Failed to delete category');
      }

      fetchCategories();

      toast({
        title: "Category deleted",
        description: `${categoryToDelete.name} has been removed from your categories list`
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Failed to delete category",
        description: "There was an error deleting the category. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  const getPriceDisplay = (category: Category) => {
    if (category.s_std_pay_type == '1') {
      return (
        <table className="border-collapse border border-gray-200 w-full">
        <thead>
          <tr className="border-b">
            <th className="px-3 py-1 text-left">{t('students.student')}</th>
            <th className="px-3 py-1 text-left">{t('categories.fixed_price')}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b">
            <td className="px-3 py-1 flex items-center gap-1">
              <User className="h-4 w-4" />
              1
            </td>
            <td className="px-3 py-1">€{category.prices?.price_fix ?? "N/A"}</td>
          </tr>
        </tbody>
      </table>
      );
    } else if (category.s_std_pay_type == '0') {
      return (
        <table className="border-collapse border border-gray-200 w-full">
          <thead>
            <tr className="border-b">
              <th className="px-3 py-1 text-left">{t('students.student')}</th>
              <th className="px-3 py-1 text-left">{t('categories.hourly_rate')}</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 10 }, (_, i) => {
              const studentCount = i + 1;
              const priceKey = `price_${studentCount}`;
              if (category.prices && category.prices[priceKey]) {
                return (
                  <tr key={studentCount} className="border-b">
                    <td className="px-3 py-1 flex items-center gap-1">
                      <User className="h-4 w-4" />
                      {studentCount}
                    </td>
                    <td className="px-3 py-1">€{category.prices[priceKey]}</td>
                  </tr>
                );
              }
              return null;
            })}
            {category.prices?.price_su && category.prices.price_su > 0 && (
              <tr>
                <td className="px-3 py-1 font-medium" colSpan={2}>
                <span className="text-xs">Additional: €{category.prices.price_su}</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      );
    } else {
      return (
        <div>{`€${category.prices?.price_1} - €${category.prices?.price_su} /h`}</div>
      );
    }
  };

  const CardComponent = small ? React.Fragment : Card;
  const extraProps = small ? {} : { className: "mb-6" };

  return (
    <CardComponent {...extraProps}>
      {!small && (
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Categories
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={small ? "p-0" : ""}>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="relative w-48">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('categories.search_categories')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <Button id="settings-categories-add-btn"
              onClick={() => setIsAddingCategory(true)}
              className="gap-1 ml-2"
              disabled={isLoading}
            >
              <Plus className="h-4 w-4" />
              {!isMobile && t('common.add')}
            </Button>
          </div>

          {isLoadingCategories ? (
            <div className="text-center py-4"> 
              <Loader2 className="mr-2 h-4 w-4 animate-spin inline-block" />
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCategories.map((category: Category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-2 rounded-md bg-secondary/50"
                >
                  <div className="flex items-center gap-2">
                    {user.account_type === 'S' && user.roleType === "school_admin" ? (
                        <Tag className="w-4 h-4" />
                    ) : (
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: category.color }}></div>
                    )}
                    <span className={`cursor-pointer p-0 h-auto text-foreground hover:text-primary ${!isMobile ? 'font-medium' : 'text-sm'}`} onClick={() => setEditingCategory(category)}>
                      {category.name}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    {/*!isMobile ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <span className="font-medium flex items-center gap-1">
                            <Info className="h-3.5 w-3.5 text-muted-foreground" />
                          </span>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2">
                          {getPriceDisplay(category)}
                        </PopoverContent>
                      </Popover>
                    ) : null*/}

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingCategory(category)}
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteCategory(category.id)}
                      className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {filteredCategories.length === 0 && !isLoadingCategories && (
                <div className="text-center p-4 text-muted-foreground">
                  {searchQuery ? "No matching categories found" : "No categories added yet"}
                </div>
              )}
            </div>
          )}
        </div>


        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('categories.confirmDelete')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('categories.confirmDeleteCategory')} "{categoryToDelete?.name}"?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className='mt-5'>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteCategory}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>



        <Dialog open={isAddingCategory} onOpenChange={(open) => {
          if (!open) {
            setIsAddingCategory(false);
            window.dispatchEvent(new CustomEvent('wizard-add-dialog-closed', { detail: { type: 'category' } }));
          } else {
            window.dispatchEvent(new CustomEvent('wizard-add-dialog-opened', { detail: { type: 'category' } }));
          }
        }}>
          <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[650px]"}>
            <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-primary" />
                {t('categories.create_new')}
              </DialogTitle>
            </DialogHeader>
            <div className='flex-1 overflow-auto p-5'>
              {user.account_type === 'C' || user.roleType === "teachers_all" ? (
                <AddCategoryForm onSubmit={handleAddCategory} onCancel={() => {
                  setIsAddingCategory(false);
                  window.dispatchEvent(new CustomEvent('wizard-add-dialog-closed', { detail: { type: 'category' } }));
                }} />
               ) : (
                <AddSchoolCategoryForm onSubmit={handleAddSchoolCategory} onCancel={() => {
                  setIsAddingCategory(false);
                  window.dispatchEvent(new CustomEvent('wizard-add-dialog-closed', { detail: { type: 'category' } }));
                }} />
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!editingCategory} onOpenChange={(open) => !open && setEditingCategory(null)}>
         <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[650px]"}>
            <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-primary" />
                {t('categories.edit')}
              </DialogTitle>
            </DialogHeader>
            {editingCategory && (
              <div className="flex-1 overflow-auto p-5">
                {user.account_type === 'C' || user.roleType === "teachers_all" ? (
                <EditCategoryForm
                  category={editingCategory}
                  onSubmit={handleUpdateCategory}
                  onCancel={() => setEditingCategory(null)}
                />
                ) : (
                   <EditSchoolCategoryForm
                  category={editingCategory}
                  onSubmit={handleUpdateSchoolCategory}
                  onCancel={() => setEditingCategory(null)}
                />
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </CardComponent>
  );
};

export default CategoriesManager;
